<template>
  <smd-one-col-layout class="claim-records-modal"
                      content-styles="claim-records-modal__content"
                      watch-scrolltop
                      @scrolling="setIsScrolling">
    <div v-if="state !== STATES.CONFIRMED"
         class="smd-header claim-records-modal__header"
         slot="header">
      <span v-if="canGoBack"
            class="ns-icon-ico028 claim-records-modal__icon-btn"
            role="button"
            tabindex="0"
            aria-label="Back"
            @click="goBackToList"
            @keydown.enter="goBackToList"/>
      <span v-else
            class="ns-icon-ico027 claim-records-modal__icon-btn"
            role="button"
            tabindex="0"
            aria-label="Close"
            @click="closeModal"
            @keydown.enter="closeModal"/>
      <h3 class="claim-records-modal__title"
          v-if="title && (!$isMobile || isScrolling)">
        {{ title }}
      </h3>
      <span v-else class="claim-records-modal__title-spacer" aria-hidden="true"/>
      <span class="claim-records-modal__icon-btn claim-records-modal__icon-btn--spacer" aria-hidden="true"/>
    </div>

    <div class="claim-records-modal__body"
         :class="bodyClass">
      <h2 class="claim-records-modal__body-title"
          v-if="$isMobile && title">
        {{ title }}
      </h2>

      <div class="claim-records-modal__loading"
           v-if="state === STATES.LOADING">
        <smd-loading-spinner/>
      </div>

      <div class="claim-records-modal__confirmed"
           v-else-if="state === STATES.CONFIRMED">
        <img class="claim-records-modal__confirmed-illustration" :src="confirmedBaby" alt="">
        <div class="claim-records-modal__confirmed-text">
          <h3 class="claim-records-modal__confirmed-title">Thanks for confirming</h3>
          <p class="claim-records-modal__confirmed-body">Your baby book is ready to access</p>
        </div>
      </div>

      <div class="claim-records-modal__no-match"
           v-else-if="state === STATES.INVALID_TOKEN">
        <img class="claim-records-modal__no-match-icon" :src="attentionIcon" alt="">
        <h3 class="claim-records-modal__no-match-title">Code no longer valid</h3>
        <p class="claim-records-modal__no-match-body">
          This QR code is invalid or no longer active. Please ask the clinic for an updated code.
        </p>
      </div>

      <div class="claim-records-modal__no-match"
           v-else-if="state === STATES.NO_MATCH">
        <img class="claim-records-modal__no-match-icon" :src="attentionIcon" alt="">
        <h3 class="claim-records-modal__no-match-title">Couldn't find your record</h3>
        <p class="claim-records-modal__no-match-body">
          Your mobile number or the details you entered may not match what's on file.
        </p>
        <div class="info-panel">
          <div class="info-panel__heading">
            <img class="info-panel__icon" :src="infoIcon" alt="">
            <span class="info-panel__heading-text">What to do on your next visit</span>
          </div>
          <ul class="info-panel__list">
            <li>Ask the clinic to update your mobile number.</li>
            <li>Request NowServing invite code from the clinic.</li>
            <li>Enter the code in the Account tab on NowServing.</li>
          </ul>
        </div>
        <smd-btn class="claim-records-modal__no-match-cta"
                 section="4"
                 variant="unselected"
                 @click="closeModal">
          Okay
        </smd-btn>
      </div>

      <template v-else-if="state === STATES.LIST">
        <p class="claim-records-modal__hint claim-records-modal__hint--list">
          We found profiles that might be yours. Review and claim, or skip for now.
        </p>
        <div class="claim-records-modal__profile-list">
          <claim-profile-card v-for="patient in patients"
                              :key="patient.id"
                              :doctor="doctor"
                              :firstname="patient.firstname"
                              :lastname="verifiedLastname(patient.id)"
                              :verified="isVerified(patient.id)"
                              :clickable="!isVerified(patient.id)"
                              @click="selectPatient(patient)"/>
        </div>
      </template>

      <template v-else-if="state === STATES.VERIFY_FORM && selectedPatient">
        <claim-profile-card :doctor="doctor"
                            :firstname="selectedPatient.firstname"/>

        <p class="claim-records-modal__hint">
          Fill out the form to confirm it's yours.
        </p>

        <form class="claim-records-modal__form" novalidate @submit.prevent="submitVerify">
          <div class="claim-records-modal__field">
            <span class="claim-records-modal__label">Last Name</span>
            <input ref="lastname"
                   v-model.trim="lastname"
                   class="claim-records-modal__input"
                   type="text"
                   autocomplete="family-name"
                   :disabled="locked || verifying">
          </div>
          <div class="claim-records-modal__field">
            <span class="claim-records-modal__label">Birthdate</span>
            <smd-date-input ref="birthdate"
                            v-model="birthdate"
                            class="claim-records-modal__input claim-records-modal__input--date"
                            icon-position="right"
                            :format-emitted-date="'MM/DD/YYYY'"
                            :disabled="locked || verifying"/>
          </div>
        </form>
      </template>
    </div>

    <div class="claim-records-modal__footer"
         v-if="state === STATES.VERIFY_FORM" slot="bottom-nav">
      <button type="button"
              class="claim-records-modal__btn claim-records-modal__btn--primary"
              :disabled="submitDisabled"
              @click="submitVerify">
        <smd-loading-spinner v-if="verifying"/>
        <span v-else>Continue</span>
      </button>
      <p v-if="locked" class="claim-records-modal__rate-limit">
        Too many attempts. Try again in 15 minutes.
      </p>
    </div>

    <div class="claim-records-modal__footer"
         v-else-if="state === STATES.LIST"
         slot="bottom-nav">
      <button type="button"
              class="claim-records-modal__btn claim-records-modal__btn--outlined"
              @click="closeModal">
        Skip for now
      </button>
    </div>
  </smd-one-col-layout>
</template>

<script>
  import {mapGetters, mapActions} from 'vuex';
  import {
    ClinicQrClaimService,
    CLINIC_QR_LOOKUP_RESULT,
    CLINIC_QR_VERIFY_RESULT,
  } from '@/services/clinic-qr-claim';
  import {
    GETTER_CLINIC_QR_DOCTOR_TOKEN,
    ACTION_CLEAR_CLINIC_QR_DOCTOR_TOKEN,
    ACTION_SHOW_TOAST,
    GETTER_FIRSTNAME,
    GETTER_LASTNAME,
    GETTER_BIRTHDATE,
  } from '@/store/types';
  import {logger, trackEvent} from '@/utils/logger';
  import GTAG_EVENT_ACTION from '@/reference/gtag-event-actions-lookup';
  import {
    GTAG_PARAMS_ORIGIN,
    GTAG_PARAMS_RESULT,
    GTAG_PARAMS_TYPE,
  } from '@/reference/gtag-params-lookup';
  import ClaimProfileCard from '@/components/clinic-qr/claim-profile-card';
  import InfoIcon from '@/assets/icons/info-outline.svg';
  import AttentionIcon from '@/assets/icons/attention-fill-big.svg';
  import ConfirmedBabyImg from '@/assets/images/clinic-qr/confirmed-baby.png';
  import scrollListener from "@/mixins/scroll-listener";
  import {bus} from '@/main';
  import {CLAIM_PROFILE_VERIFIED} from '@/constants/bus';

  const STATES = {
    LOADING: 'loading',
    INVALID_TOKEN: 'invalid_token',
    NO_MATCH: 'no_match',
    LIST: 'list',
    VERIFY_FORM: 'verify_form',
    CONFIRMED: 'confirmed',
  };

  const STATE_TO_ORIGIN = {
    [STATES.LIST]: GTAG_PARAMS_ORIGIN.ClaimRecordsModal_List,
    [STATES.VERIFY_FORM]: GTAG_PARAMS_ORIGIN.ClaimRecordsModal_VerifyForm,
    [STATES.NO_MATCH]: GTAG_PARAMS_ORIGIN.ClaimRecordsModal_NoMatch,
    [STATES.INVALID_TOKEN]: GTAG_PARAMS_ORIGIN.ClaimRecordsModal_InvalidToken,
    [STATES.CONFIRMED]: GTAG_PARAMS_ORIGIN.ClaimRecordsModal_Confirmed,
  };

  const BABYBOOK_MAX_AGE = 18;
  const CONFIRMED_AUTO_DISMISS_MS = 3000;

  export default {
    name: 'claim-records-modal',
    mixins: [scrollListener],
    components: {ClaimProfileCard},
    data() {
      return {
        STATES,
        state: STATES.LOADING,
        doctor: null,
        patients: [],
        verifiedPatients: {},
        selectedPatient: null,
        lastname: '',
        birthdate: '',
        verifying: false,
        locked: false,
        infoIcon: InfoIcon,
        attentionIcon: AttentionIcon,
        confirmedBaby: ConfirmedBabyImg,
        confirmedDismissTimer: null,
      };
    },
    computed: {
      ...mapGetters({
        clinicQrDoctorToken: GETTER_CLINIC_QR_DOCTOR_TOKEN,
        accountFirstname: GETTER_FIRSTNAME,
        accountLastname: GETTER_LASTNAME,
        accountBirthdate: GETTER_BIRTHDATE,
      }),
      bodyClass() {
        if (this.state === STATES.CONFIRMED) return 'claim-records-modal__body--confirmed';
        return '';
      },
      isSinglePatientFlow() {
        return this.patients.length === 1;
      },
      canGoBack() {
        return this.state === STATES.VERIFY_FORM && !this.isSinglePatientFlow;
      },
      title() {
        if (this.state === STATES.VERIFY_FORM) return 'Claim this Profile';
        if (this.state === STATES.LIST) return 'Profiles to Review';
        return '';
      },
      submitDisabled() {
        if (this.verifying || this.locked) return true;
        return !this.lastname || !this.birthdate;
      },
    },
    created() {
      this.lookupProfiles();
    },
    beforeDestroy() {
      if (this.confirmedDismissTimer) {
        clearTimeout(this.confirmedDismissTimer);
        this.confirmedDismissTimer = null;
      }
    },
    watch: {
      state: {
        handler(newState) {
          if (newState === STATES.LOADING) return;
          this.trackFormView();
        },
        immediate: true,
      },
    },
    methods: {
      ...mapActions({
        clearDoctorToken: ACTION_CLEAR_CLINIC_QR_DOCTOR_TOKEN,
        showToast: ACTION_SHOW_TOAST,
      }),
      async lookupProfiles() {
        this.state = STATES.LOADING;

        try {
          const {data} = await ClinicQrClaimService.lookupProfiles(this.clinicQrDoctorToken);

          if (data.result === CLINIC_QR_LOOKUP_RESULT.INVALID_TOKEN) {
            this.state = STATES.INVALID_TOKEN;
            return;
          }

          this.doctor = data.doctor || null;
          this.patients = data.patients || [];

          this.trackScan(this.patients.length);

          if (this.patients.length === 0) {
            this.state = STATES.NO_MATCH;
          } else if (this.patients.length === 1) {
            this.selectedPatient = this.patients[0];
            const autoClaimed = await this.tryAutoClaim();
            if (!autoClaimed) this.state = STATES.VERIFY_FORM;
          } else {
            this.state = STATES.LIST;
          }
        } catch (error) {
          logger.error('clinicqr lookup failed', {message: error.message});
          this.state = STATES.INVALID_TOKEN;
        }
      },
      selectPatient(patient) {
        if (this.isVerified(patient.id)) return;
        this.selectedPatient = patient;
        this.lastname = '';
        this.birthdate = '';
        this.locked = false;
        this.state = STATES.VERIFY_FORM;
      },
      goBackToList() {
        this.selectedPatient = null;
        this.state = STATES.LIST;
      },
      isVerified(patientId) {
        return !!this.verifiedPatients[patientId];
      },
      verifiedLastname(patientId) {
        const entry = this.verifiedPatients[patientId];
        return entry ? entry.lastname : '';
      },
      trackScan(profileCount) {
        const payload = {
          doctorId: this.clinicQrDoctorToken,
          profileCount,
          type: GTAG_PARAMS_TYPE.QrCodeClaimProfile,
          source: GTAG_PARAMS_ORIGIN.ClaimRecordsModal,
        };
        this.$gtag.event(GTAG_EVENT_ACTION.View, payload);
        trackEvent(GTAG_EVENT_ACTION.View, payload);
      },
      trackFormView() {
        const payload = {
          doctorId: this.clinicQrDoctorToken,
          type: GTAG_PARAMS_TYPE.QrCodeClaimProfile,
          source: STATE_TO_ORIGIN[this.state],
        };
        this.$gtag.event(GTAG_EVENT_ACTION.View, payload);
        trackEvent(GTAG_EVENT_ACTION.View, payload);
      },
      trackSubmit(result, source = STATE_TO_ORIGIN[this.state]) {
        const payload = {
          doctorId: this.clinicQrDoctorToken,
          result,
          type: GTAG_PARAMS_TYPE.QrCodeClaimProfile,
          source,
        };
        this.$gtag.event(GTAG_EVENT_ACTION.Click, payload);
        trackEvent(GTAG_EVENT_ACTION.Click, payload);
      },
      firstnameMatchesSelected() {
        const patientFirstname = this.selectedPatient && this.selectedPatient.firstname;
        if (!patientFirstname || !this.accountFirstname) return false;
        return patientFirstname.trim().toLowerCase() === this.accountFirstname.trim().toLowerCase();
      },
      async tryAutoClaim() {
        const lastname = this.accountLastname;
        const birthdate = this.accountBirthdate;
        if (!lastname || !birthdate) return false;
        if (!this.firstnameMatchesSelected()) return false;

        try {
          const {data} = await ClinicQrClaimService.verifyProfile({
            doctorUserId: this.clinicQrDoctorToken,
            patientId: this.selectedPatient.id,
            lastname,
            birthdate,
          });

          if (data.result === CLINIC_QR_VERIFY_RESULT.SUCCESS) {
            this.lastname = lastname;
            this.birthdate = birthdate;
            this.trackSubmit(GTAG_PARAMS_RESULT.Success, GTAG_PARAMS_ORIGIN.ClaimRecordsModal_AutoClaim);
            this.handleVerifySuccess();
            return true;
          }

          if (data.result === CLINIC_QR_VERIFY_RESULT.RATE_LIMITED) {
            this.locked = true;
            this.trackSubmit(GTAG_PARAMS_RESULT.RateLimited, GTAG_PARAMS_ORIGIN.ClaimRecordsModal_AutoClaim);
            return false;
          }

          this.trackSubmit(GTAG_PARAMS_RESULT.Fail, GTAG_PARAMS_ORIGIN.ClaimRecordsModal_AutoClaim);
          return false;
        } catch (error) {
          this.trackSubmit(GTAG_PARAMS_RESULT.Fail, GTAG_PARAMS_ORIGIN.ClaimRecordsModal_AutoClaim);
          logger.error('clinicqr auto-claim failed', {message: error.message});
          return false;
        }
      },
      async submitVerify() {
        if (this.submitDisabled) return;

        this.verifying = true;

        try {
          const {data} = await ClinicQrClaimService.verifyProfile({
            doctorUserId: this.clinicQrDoctorToken,
            patientId: this.selectedPatient.id,
            lastname: this.lastname,
            birthdate: this.birthdate,
          });

          if (data.result === CLINIC_QR_VERIFY_RESULT.SUCCESS) {
            this.trackSubmit(GTAG_PARAMS_RESULT.Success);
            this.handleVerifySuccess();
            return;
          }

          if (data.result === CLINIC_QR_VERIFY_RESULT.RATE_LIMITED) {
            this.trackSubmit(GTAG_PARAMS_RESULT.RateLimited);
            this.locked = true;
            return;
          }

          this.trackSubmit(GTAG_PARAMS_RESULT.Fail);
          await this.showToast({text: 'Verification failed. Please try again.', theme: 'danger', position: 'top'});
        } catch (error) {
          this.trackSubmit(GTAG_PARAMS_RESULT.Fail);
          logger.error('clinicqr verify failed', {message: error.message});
          await this.showToast({text: 'Verification failed. Please try again.', theme: 'danger', position: 'top'});
        } finally {
          this.verifying = false;
        }
      },
      handleVerifySuccess() {
        const babybookEligible = this.isBabybookAge(this.birthdate);

        this.verifiedPatients = {
          ...this.verifiedPatients,
          [this.selectedPatient.id]: {lastname: this.lastname, babybook: babybookEligible},
        };

        bus.$emit(CLAIM_PROFILE_VERIFIED);

        // if (babybookEligible) {
        this.state = STATES.CONFIRMED;
        this.confirmedDismissTimer = setTimeout(() => {
          this.closeModal();
        }, CONFIRMED_AUTO_DISMISS_MS);
        //   return;
        // }

        // this.showToast({text: 'Profile Verified.', theme: 'success', position: 'top'});
        //
        // if (this.isSinglePatientFlow) {
        //   this.closeModal();
        //   return;
        // }
        //
        // this.selectedPatient = null;
        // this.lastname = '';
        // this.birthdate = '';
        // this.state = STATES.LIST;
      },
      isBabybookAge(birthdate) {
        if (!birthdate) return false;
        const parts = birthdate.split(/[-/]/);
        if (parts.length !== 3) return false;
        const [mm, dd, yyyy] = parts.map((n) => parseInt(n, 10));
        const dob = new Date(yyyy, mm - 1, dd);
        if (Number.isNaN(dob.getTime())) return false;
        const ageMs = Date.now() - dob.getTime();
        const years = ageMs / (1000 * 60 * 60 * 24 * 365.25);
        return years >= 0 && years <= BABYBOOK_MAX_AGE;
      },
      closeModal() {
        if (this.confirmedDismissTimer) {
          clearTimeout(this.confirmedDismissTimer);
          this.confirmedDismissTimer = null;
        }
        this.clearDoctorToken();
        this.$modal.close(true);
      },
    },
  };
</script>

<style lang="scss">
  .claim-records-modal {
    width: 100%;
    max-width: 620px;

    &__header {
      display: grid;
      grid-template-columns: 40px 1fr 40px;
      align-items: center;
      gap: space(s);
      height: 56px;
      padding: 0 space(m);
      background: $color-white;
    }

    &__title {
      margin: 0;
      font-family: 'Inter', sans-serif;
      font-weight: 700;
      font-size: 19px;
      line-height: 1;
      color: $color-gray-90;
      text-align: center;
      text-transform: none;
    }

    &__icon-btn {
      @include border-radius(8px);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      color: $color-gray-90;
      font-size: 22px;
      cursor: pointer;

      &:hover {
        background: $color-gray-10;
      }

      &--spacer {
        cursor: default;
        pointer-events: none;
      }
    }

    &__body {
      @extend %layout--flex-col;
      gap: space(l);
      padding: space(m-base) space(l) space(l);

      @include responsive() {
        padding: 0;
      }

      &--confirmed {
        height: 100%;
        justify-content: center;
      }
    }

    &__loading {
      @extend %layout--flex-row-center-xy;
      padding: space(xl) 0;
    }

    &__confirmed {
      @extend %layout--flex-col-center-y;
      gap: 18px;
      padding: space(m-base);
      text-align: center;
    }

    &__confirmed-illustration {
      width: 275px;
      max-width: 100%;
      height: auto;
      display: block;
    }

    &__confirmed-text {
      @extend %layout--flex-col-center-y;
      gap: space(s);
      max-width: 320px;
    }

    &__confirmed-title {
      @extend %smd-font-family-bold;
      margin: 0;
      font-size: font(xl);
      line-height: 1;
      color: #565af4;
      text-transform: none;
    }

    &__confirmed-body {
      @extend %smd-font-family;
      margin: 0;
      font-size: font(base);
      line-height: 20px;
      color: $color-black;
      text-transform: none;
    }

    &__body-title {
      @extend %smd-font-family-bold;
      margin: 0;
      font-size: font(xl);
      line-height: 1.1;
      color: $color-black;
      text-transform: none;
    }

    &__title-spacer {
      flex: 1;
      min-width: 0;
    }

    &__hint {
      @extend %smd-font-family;
      margin: 0;
      font-size: font(base);
      line-height: 21px;
      color: $color-gray-70;
      text-transform: none;
    }

    &__hint--list {
      color: $color-gray-80;
      line-height: 20px;
    }

    &__no-match {
      @extend %layout--flex-col-center-y;
      gap: space(l);
      padding: space(m-base) 0 space(s);
    }

    &__no-match-icon {
      width: 80px;
      height: 80px;
      display: block;
    }

    &__no-match-title {
      @extend %smd-font-family-bold;
      margin: 0;
      font-size: font(xl);
      color: $color-orange;
      text-align: center;
      text-transform: none;
    }

    &__no-match-body {
      @extend %smd-font-family;
      margin: 0;
      font-size: font(base);
      line-height: 20px;
      color: $color-black;
      text-align: center;
      text-transform: none;
    }

    &__no-match-cta {
      width: 100%;
      max-width: 320px;
      margin: space(m-base) auto 0;
    }

    &__profile-list {
      @extend %layout--flex-col;
      gap: 12px;
    }

    &__form {
      @extend %layout--flex-col;
      gap: space(l);
    }

    &__field {
      @extend %layout--flex-col;
      gap: space(s);
    }

    &__label {
      @extend %smd-font-family-bold;
      font-size: font(base);
      color: $color-black;
      text-transform: none;
    }

    &__input {
      @extend %smd-font-family;
      @include border-radius(radius(normal));
      width: 100%;
      min-width: 0;
      height: 40px;
      padding: space(s) 12px;
      background: $color-gray-10;
      border: 1px solid $color-gray-40;
      font-size: font(base);
      color: $color-black;
      box-sizing: border-box;
      text-align: left;
      text-transform: none;
      -webkit-appearance: none;
      appearance: none;

      &:focus {
        outline: none;
        border-color: $color-gray-90;
      }

      &:disabled {
        opacity: 0.6;
      }
    }

    &__input--date {
      position: relative;
      padding: 0;
      cursor: text;

      &:focus-within {
        border-color: $color-gray-90;
      }

      input {
        @extend %smd-font-family;
        @include border-radius(0);
        width: 100%;
        height: 100%;
        margin: 0;
        padding: space(s) 40px space(s) 12px !important;
        background: transparent;
        border: none;
        box-shadow: none;
        font-size: font(base);
        color: $color-black;
        box-sizing: border-box;
        -webkit-appearance: none;
        appearance: none;

        &:focus {
          outline: none;
        }
      }

      .date-icon {
        top: 0 !important;
        color: $color-gray-70;
      }
    }

    &__footer {
      @extend %layout--flex-col-center-y;
      gap: 12px;
      padding: space(m-base);
      background: $color-white;
    }

    &__btn {
      @extend %smd-font-family-bold;
      @include border-radius(50px);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: space(xs);
      width: 100%;
      max-width: 320px;
      height: 48px;
      padding: 14px space(m);
      font-size: font(l) - 1;
      line-height: 20px;
      text-transform: none;
      cursor: pointer;

      &--primary {
        background: #565af4;
        border: none;
        color: $color-white;

        &:disabled {
          background: #ddddeb;
          color: $color-white;
          cursor: not-allowed;
        }
      }

      &--outlined {
        background: $color-white;
        border: 1px solid #dfe0ff;
        color: #565af4;
      }
    }

    &__rate-limit {
      margin: 0;
      font-family: 'Inter', sans-serif;
      font-size: 14px;
      color: $color-orange;
      text-align: center;
      text-transform: none;
    }

    .info-panel {
      @extend %layout--flex-col;
      @include border-radius(14px);
      width: 100%;
      background: $color-gray-10;
      padding: space(m);
      gap: space(m);

      &__heading {
        @extend %layout--flex-row-center-x;
        gap: space(s);
      }

      &__heading-text {
        @extend %smd-font-family-bold;
        font-size: font(base);
        line-height: 16px;
        color: $color-black;
        text-transform: none;
      }

      &__icon {
        width: 22px;
        height: 22px;
        display: block;
      }

      &__list {
        @extend %smd-font-family;
        margin: 0;
        padding-left: 22px;
        font-size: font(base);
        line-height: 20px;
        color: $color-black;
        text-transform: none;

        li {
          margin: 0;

          & + li {
            margin-top: 14px;
          }
        }
      }
    }
  }

</style>
